import {
  createSlice,
  PayloadAction,
  ActionReducerMapBuilder,
} from "@reduxjs/toolkit";
import {
  fetchAllBuilds,
  fetchBuild,
  upsertBuild,
  createHotBuildFromS3,
  fetchUnreleasedS3Builds,
} from "../actions/buildActions";

// Build interface
export interface UnrealAppBuild {
  id: string;
  build_name: string;
  entries: Array<{
    platform: string;
    repository_type: string;
    key: string;
  }>;
  deleted_at?: string | null;
  s3_build_is_hot: boolean | null;
  build_notes?: string | null;
}

// Uncut build interface: something for which there's no build_id;
// but there is something in our big build bucket.
export interface UncutBuild {
  s3_path: string;
  build_name: string;
}

// State interface
interface BuildState {
  builds: UnrealAppBuild[];
  currentBuild: UnrealAppBuild | null;
  uncutBuilds: UncutBuild[];
  loading: boolean;
  error: string | null;
}

const initialState: BuildState = {
  builds: [],
  currentBuild: null,
  uncutBuilds: [],
  loading: false,
  error: null,
};

const buildSlice = createSlice({
  name: "builds",
  initialState,
  reducers: {
    clearCurrentBuild(state) {
      state.currentBuild = null;
    },
    clearUncutBuilds(state) {
      state.uncutBuilds = [];
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<BuildState>) => {
    // Handle fetchAllBuilds
    builder
      .addCase(fetchAllBuilds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllBuilds.fulfilled,
        (state, action: PayloadAction<UnrealAppBuild[]>) => {
          state.loading = false;
          state.builds = action.payload;
        }
      )
      .addCase(fetchAllBuilds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch builds";
      });

    // Handle fetchBuild
    builder
      .addCase(fetchBuild.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchBuild.fulfilled,
        (state, action: PayloadAction<UnrealAppBuild>) => {
          state.loading = false;
          state.currentBuild = action.payload;
          const idx = state.builds.findIndex((b) => b.id === action.payload.id);
          if (idx >= 0) state.builds[idx] = action.payload;
          else state.builds.unshift(action.payload);
        }
      )
      .addCase(fetchBuild.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch build";
      });

    // upsertBuild (unchanged, but keeps builds[] in sync)
    builder
      .addCase(upsertBuild.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        upsertBuild.fulfilled,
        (state, action: PayloadAction<UnrealAppBuild>) => {
          state.loading = false;
          state.currentBuild = action.payload;
          const idx = state.builds.findIndex((b) => b.id === action.payload.id);
          if (idx >= 0) state.builds[idx] = action.payload;
          else state.builds.unshift(action.payload);
        }
      )
      .addCase(upsertBuild.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create/update build";
      });

    builder
      .addCase(fetchUnreleasedS3Builds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchUnreleasedS3Builds.fulfilled,
        (state, action: PayloadAction<UncutBuild[]>) => {
          state.loading = false;
          state.uncutBuilds = action.payload ?? [];
        }
      )
      .addCase(fetchUnreleasedS3Builds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch uncut builds";
      });

    // add to builds[] and remove source uncut by s3_path
    builder
      .addCase(createHotBuildFromS3.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createHotBuildFromS3.fulfilled,
        (state, action: PayloadAction<UnrealAppBuild>) => {
          state.loading = false;
          state.currentBuild = action.payload;

          const idx = state.builds.findIndex((b) => b.id === action.payload.id);
          if (idx >= 0) state.builds[idx] = action.payload;
          else state.builds.unshift(action.payload);

          const src = (action as any)?.meta?.arg?.uncut?.s3_path as string | undefined;
          if (src) {
            state.uncutBuilds = state.uncutBuilds.filter(u => u.s3_path !== src);
          }
        }
      )
      .addCase(createHotBuildFromS3.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create hot build";
      });
  },
});

export const { clearCurrentBuild, clearUncutBuilds } = buildSlice.actions;
export default buildSlice.reducer;